import { formatPrice } from '@sifly/shared-utils'

/**
 * Sale treatment for product cards (grid, carousel, upsell). One definition so
 * every card renders sale pricing identically and agrees with the PDP
 * (apps/storefront/.../ProductDetail.tsx): the effective price turns sale-red
 * with the compare-at price struck through, and a "Sale" pill sits on the image.
 *
 * Reads the precomputed `onSale` / `compareAtPrice` the storefront adapters
 * already attach to every StorefrontProduct (deriveDisplayPrice /
 * deriveDealerDisplayPrice — min effective price across active SKUs, resolved
 * per catalog). Cards never re-resolve pricing; they render what they're handed.
 *
 * Colours read the fixed sale-pricing tokens (--color-sale{,-surface,-strong})
 * from packages/shared-ui/src/theme/tokens.css — the SAME tokens the PDP
 * (apps/storefront/.../ProductDetail.tsx) consumes, so the two surfaces can't
 * drift. Warm coral, not saturated red (branding-and-sites.md § Theme Token System).
 */

export function SaleBadge({ className }: { className?: string }) {
  return (
    <span
      className={`rounded-full bg-[var(--color-sale-surface)] px-2 py-0.5 text-xs font-medium text-[var(--color-sale-strong)] ${className ?? ''}`}
    >
      Sale
    </span>
  )
}

type CardPriceProps = {
  displayPrice: number | null
  compareAtPrice: number | null
  /** "From $X" when the lowest active SKU price isn't the only one. */
  priceFromMultiple: boolean
  onSale: boolean
  /** Card-owned sizing/colour for the base line (e.g. "mt-1 text-sm text-[var(--color-text-secondary)]"). */
  className?: string
}

export function CardPrice({ displayPrice, compareAtPrice, priceFromMultiple, onSale, className }: CardPriceProps) {
  return (
    <p className={className}>
      {priceFromMultiple ? 'From ' : ''}
      <span className={onSale ? 'text-[var(--color-sale)]' : undefined}>{formatPrice(displayPrice)}</span>
      {compareAtPrice != null && (
        <span className="ml-1.5 text-[var(--color-text-muted)] line-through">{formatPrice(compareAtPrice)}</span>
      )}
    </p>
  )
}

/**
 * Same sale treatment as CardPrice but for surfaces that precompute their price
 * strings server-side (the serializable `featured_products` section). Kept here
 * so every card-side reference to the sale-pricing tokens lives in one file
 * alongside SaleBadge and CardPrice.
 */
export function SalePriceLabel({
  priceLabel,
  compareAtLabel,
  onSale,
  className,
}: {
  priceLabel: string
  compareAtLabel?: string | null
  onSale?: boolean
  className?: string
}) {
  return (
    <p className={className}>
      <span className={onSale ? 'text-[var(--color-sale)]' : undefined}>{priceLabel}</span>
      {compareAtLabel != null && (
        <span className="ml-1.5 text-[var(--color-text-muted)] line-through">{compareAtLabel}</span>
      )}
    </p>
  )
}
